<template>
    <div>
        <div v-if="!showAddEditForm && !showEditPortfolioForm" class="mt-4 w-full">
            <div class="flex flex-wrap items-center">
                <span class="text-uppercase font-bold mr-1 mt-2 text-sm">Portfolio:</span>
                <div class="inline-block mr-2 mt-0 sm:mt-2 xs:w-full">
                    <select @change="onChangePortfolio" v-model="currentPortfolio" class="form-control form-control-sm align-bottom bg-white" aria-label="Portfolio">
                        <option :value="portfolio" v-for="portfolio in orderedItems(portfolios)">{{ portfolio.caption }}</option>
                        <option value="">All Porfolios (Summary)</option>
                    </select>
                </div>
                <div class="mt-2 xs:pt-3 xs:mt-3 xs:border-t xs:w-full inline-block ml-auto print:hidden">
                    <span class="btn btn-blue btn-sm inline-block xs:mx-auto" @click="showAddEditForm = true">
                        Add or Edit Portfolios
                    </span>
                </div>
            </div>

            <div v-if="portfolioItemsError">
                <alert-box>
                    <template #body>
                        There is no data for this portfolio.
                    </template>
                </alert-box>
            </div>
            <div v-else>
                <div v-if="showAllPortfolios">
                    <div class="border-t mt-3 py-1" v-if="portfolioData">
                        <div class="pt-2 mb-1">
                            <summary-view :all-summary="true" :portfolio="portfolioData"></summary-view>
                        </div>
                        <all-portfolios-summary
                            @changePortfolio="onEmitPortfolioChange"
                            @changePeriod="onEmitChangeSummaryPeriod"
                            :defaultPeriod="period"
                            :summaryData="portfolioData"
                        ></all-portfolios-summary>
                    </div>
                    <alert-box v-else>
                        <template #body>
                            There is no data for All Porfolios (Summary).
                        </template>
                    </alert-box>
                    <!-- <spinner v-else></spinner> -->
                </div>
                <div v-else class="relative">
                    <div v-show="dataLoading" class="absolute z-50 w-full h-full bg-spinner-ghost flex flex-col items-center">
                        <spinner class="mb-1"></spinner>
                    </div>
                    <div class="border-t mt-3 py-1" v-if="portfolioData">
                        <div class="py-2 mb-1">
                            <summary-view @onGetAllPortfoliosSummary="getAllPortfoliosSummary" :portfolio="portfolioData"></summary-view>
                        </div>
                        <div class="w-full print:hidden" v-if="portfolioData.items.length">
                            <div class="ml-auto text-right">
                                <button class="items-center text-xs lg:text-sm btn btn-sm text-blue-600 hover:text-blue-400 mb-2 mt-0 sm:mt-2" @click="openFlipcharts">
                                    <span class="mr-1">FlipCharts</span> <i class="fas fa-chart-area"></i>
                                </button>
                            </div>
                        </div>
                        <trade-tracker-table
                            @editPortfolio="onEditPortfolio"
                            @updateView="onChangeView"
                            @updatePeriod="onChangePeriod"
                            @reloadPortfolio="onReloadPortfolio"
                            @saveNotes="saveNotes"
                            :defaultView="view"
                            :defaultPeriod="period"
                            :portfolio="portfolioData"
                            :portfolios="portfolios"
                            :buttons="buttonOptions"
                            :types="symbolTypes"
                        ></trade-tracker-table>
                    </div>
                    <spinner v-else></spinner>
                </div>
            </div>
        </div>
        <div v-else-if="showEditPortfolioForm">
            <edit-portfolio-form
                @save="updatePortfolio"
                @close="showEditPortfolioForm = false"
                :initial-portfolio="portfolioDataEdit"
            ></edit-portfolio-form>
        </div>
        <div v-else>
            <trade-tracker-form
                @save="updatePortfolios"
                @close="showAddEditForm = false"
                :initial-portfolios="portfolios"
                :jims="jims"
            ></trade-tracker-form>
        </div>

        <flash-message :secondsToDisplay="3" ref="flash"></flash-message>

    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import SummaryView from './TradeTracker/SummaryView';
import TradeTrackerTable from './TradeTracker/TradeTrackerTable';
import TradeTrackerForm from './TradeTracker/TradeTrackerForm';
import AllPortfoliosSummary from './TradeTracker/AllPortfoliosSummary';
import EditPortfolioForm from './TradeTracker/EditPortfolioForm';
import { useFlipcharts } from '../DataTable/mixins/FlipCharts';

const props = defineProps({
    initialPortfolios: {
        type: Array,
        required: true
    },
    buttonOptions: Object,
    symbolTypes: Object,
    jims: Boolean,
});

const flash = ref(null);
const { flipcharts } = useFlipcharts();

const portfolios = ref(props.initialPortfolios || []);
const currentPortfolio = ref(findLastViewedPortfolio() || props.initialPortfolios[0]);
const portfolioData = ref(null);
const portfolioDataEdit = ref(null);

const showAddEditForm = ref(false);
const showEditPortfolioForm = ref(false);
const showAllPortfolios = ref(false);

const view = ref(null);
const period = ref(null);
const dataLoading = ref(false);

const portfolioItemsError = ref(false);

function findLastViewedPortfolio() {
    const last = window.localStorage.getItem('lastViewedPortfolio');
    if (last) {
        if (last == 'all') {
            return null;
        } else {
            return props.initialPortfolios.find(c => c.id === parseInt(last)) || props.initialPortfolios[0];
        }
    }
}

function openFlipcharts() {
    if (portfolioData.value && portfolioData.value.items.length > 0) {
        const symbols = portfolioData.value.items.map(row => {
            if (props.symbolTypes[row.symbolType] && props.symbolTypes[row.symbolType].section === 'future-options') {
                return row.underlyingFuture;
            }
            return row.symbol;
        });
        flipcharts(symbols);
    }
}

function orderedItems(items) {
    return items.filter(item => item['sort_order']);
}

function onEditPortfolio(portfolioId) {
    dataLoading.value = true;

    // get portfolio items to edit
    portfolioDataEdit.value = null;
    const route = window.route('api.trade-tracker.show-portfolio', { id: currentPortfolio.value['id'] });
    axios.get(route, { params: { 'view': 'portfolio' } })
        .then(response => {
            if (response.data && response.data.data && response.data.data.length > 0) {
                portfolioDataEdit.value = response.data.data[0];
                showEditPortfolioForm.value = true;
                dataLoading.value = false;
            }
        })
        .catch(error => {
            console.error(error);
            dataLoading.value = false;
        });
}

function onEmitPortfolioChange(portfolioId) {
    currentPortfolio.value = portfolios.value.find(p => p.id == portfolioId);
    onChangePortfolio();
}

function onEmitChangeSummaryPeriod(newPeriod) {
    period.value = newPeriod;
    getAllPortfoliosSummary();
}

function onChangeView(newView) {
    view.value = newView;
    portfolioItems();
}

function onChangePeriod(newPeriod) {
    period.value = newPeriod;
    portfolioItems();
}

function onReloadPortfolio() {
    if (currentPortfolio.value) {
        portfolioItems();
    }
}

function onChangePortfolio() {
    showAllPortfolios.value = false;
    if (currentPortfolio.value) {
        view.value = currentPortfolio.value.default_view;
        portfolioItems();
    } else {
        getAllPortfoliosSummary();
    }
}

function getAllPortfoliosSummary() {
    portfolioData.value = null;
    currentPortfolio.value = "";
    const route = window.route('api.trade-tracker.portfolios');
    axios.get(route, { params: { 'eod': period.value == 'eod' } })
        .then(response => {
            if (response.data && response.data.data.portfolios && response.data.data.portfolios.length > 0) {
                showAllPortfolios.value = true;
                portfolioData.value = response.data.data;
            } else {
                showAllPortfolios.value = true;
                portfolioData.value = null;
            }
        })
        .catch(error => {
            console.error(error);
        });
}

function portfolioItems() {
    portfolioData.value = null;
    portfolioItemsError.value = false;

    const route = window.route('api.trade-tracker.show-portfolio', { id: currentPortfolio.value['id'] });
    axios.get(route, {
        params: {
            'view': view.value || currentPortfolio.value.default_view,
            'eod': period.value == 'eod'
        }
    })
        .then(response => {
            if (response.data && response.data.data && response.data.data.length > 0) {
                portfolioData.value = response.data.data[0];
            } else {
                portfolioItemsError.value = true;
            }
        })
        .catch(error => {
            console.error(error);
            portfolioItemsError.value = true;
        });
}

function updatePortfolio(newPortfolios) {
    showEditPortfolioForm.value = false;
    flash.value.display('Updated portfolio and transactions');
    portfolios.value = newPortfolios || props.initialPortfolios;
    currentPortfolio.value = portfolios.value.find(p => p.id == (currentPortfolio.value.id || 0));
    onChangePortfolio();
}

function updatePortfolios(newPortfolios) {
    flash.value.display('Updated portfolios');

    // We're attempting to find a new portfolio that was created to "select" it
    const existingPortfolioIds = portfolios.value.map(c => c.id);
    const newPortfolio = newPortfolios.filter(c => !existingPortfolioIds.includes(c.id));

    portfolios.value = newPortfolios;
    showAddEditForm.value = false;
    showEditPortfolioForm.value = false;
    showAllPortfolios.value = false;

    if (newPortfolio.length > 0) {
        currentPortfolio.value = newPortfolio[0];
        portfolioData.value = null;
    } else {
        currentPortfolio.value = newPortfolios[0];
    }
    view.value = currentPortfolio.value.default_view;
    portfolioItems();
}

function saveNotes(portfolio_notes) {
    currentPortfolio.value.portfolio_notes = portfolio_notes;
    const route = window.route('api.trade-tracker.update-portfolio');
    axios.post(route, {
        id: currentPortfolio.value.id,
        portfolio_notes: currentPortfolio.value.portfolio_notes,
    })
        .then(response => {
            flash.value.display('Saved portfolio notes');
        })
        .catch(error => {
            console.error(error);
            flash.value.error('Error saving your notes...');
        });
}

watch(currentPortfolio, (portfolio) => {
    window.localStorage.setItem('lastViewedPortfolio', portfolio ? portfolio.id : 'all');
});

onMounted(() => {
    currentPortfolio.value = findLastViewedPortfolio();
    onChangePortfolio();
});
</script>
